/**
 * StepCard - Composant pour afficher une étape de la timeline d'exécution
 *
 * Affiche:
 * - Icône de statut (✅ ⏳ ❌ ⏸️ ⚪ ⏭️)
 * - Nom de l'étape
 * - Durée
 * - État visuel (succeeded, running, failed, waiting, pending, skipped)
 */

import React from 'react';

export interface ExecutionStep {
  id: string;
  run_id: string;
  step_name: string;
  step_order: number;
  step_display_name: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'waiting_approval';
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  logs?: string;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface StepCardProps {
  step: ExecutionStep;
  isSelected: boolean;
  onClick: () => void;
  avgDuration?: string; // Durée moyenne historique
}

const StepCard: React.FC<StepCardProps> = ({ step, isSelected, onClick, avgDuration }) => {
  /**
   * Obtenir l'icône selon le statut
   */
  const getStatusIcon = (): string => {
    switch (step.status) {
      case 'succeeded':
        return '✅';
      case 'running':
        return '⏳';
      case 'failed':
        return '❌';
      case 'waiting_approval':
        return '⏸️';
      case 'pending':
        return '⚪';
      case 'skipped':
        return '⏭️';
      default:
        return '⚪';
    }
  };

  /**
   * Formater la durée
   */
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  /**
   * Obtenir la classe CSS selon le statut
   */
  const getStatusClass = (): string => {
    return `step-card-${step.status}`;
  };

  /**
   * Calculer la différence avec la moyenne
   */
  const getDurationComparison = (): { diff: number; indicator: string } | null => {
    if (!step.duration_seconds || !avgDuration) return null;

    const avg = parseFloat(avgDuration);
    const current = step.duration_seconds;
    const diff = ((current - avg) / avg) * 100;

    if (Math.abs(diff) < 5) {
      return { diff, indicator: '≈' }; // Similaire
    } else if (diff > 0) {
      return { diff, indicator: '▲' }; // Plus lent
    } else {
      return { diff, indicator: '▼' }; // Plus rapide
    }
  };

  const comparison = getDurationComparison();

  return (
    <div
      className={`step-card ${getStatusClass()} ${isSelected ? 'step-card-selected' : ''}`}
      onClick={onClick}
    >
      <div className="step-card-icon">{getStatusIcon()}</div>
      <div className="step-card-content">
        <div className="step-card-name">{step.step_display_name}</div>
        {step.duration_seconds !== undefined && step.duration_seconds > 0 && (
          <div className="step-card-duration">
            {formatDuration(step.duration_seconds)}
            {comparison && (
              <span
                className={`step-card-comparison ${
                  comparison.indicator === '▼'
                    ? 'comparison-faster'
                    : comparison.indicator === '▲'
                    ? 'comparison-slower'
                    : 'comparison-similar'
                }`}
                title={`${Math.abs(comparison.diff).toFixed(0)}% ${
                  comparison.indicator === '▼' ? 'faster' : comparison.indicator === '▲' ? 'slower' : 'similar'
                } than average (${avgDuration}s)`}
              >
                {comparison.indicator}
              </span>
            )}
          </div>
        )}
        {step.status === 'running' && !step.duration_seconds && (
          <div className="step-card-duration step-card-running-label">En cours...</div>
        )}
        {avgDuration && step.status === 'pending' && (
          <div className="step-card-duration step-card-avg">~{avgDuration}s</div>
        )}
      </div>
    </div>
  );
};

export default StepCard;
